import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactForm from './components/ContactForm';
import ContactMethods from './components/ContactMethods';
import ProjectEstimator from './components/ProjectEstimator';
import SocialProof from './components/SocialProof';
import LocationInfo from './components/LocationInfo';
import ConsultationProcess from './components/ConsultationProcess';
import { useModal } from '../../contexts/ModalContext';

const ContactEngagementHub = () => {
  const { openConsultation } = useModal();
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'contact';
  });
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const tabsRef = useRef(null);

  const handleTabSelect = (tabId) => {
    setActiveTab(tabId);
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const tabs = [
    { id: 'contact', label: 'Contact Form', icon: 'Mail' },
    { id: 'methods', label: 'Contact Methods', icon: 'Phone' },
    { id: 'estimator', label: 'Project Estimator', icon: 'Calculator' },
    { id: 'process', label: 'Our Process', icon: 'GitBranch' }
  ];

  const heroStats = [
    { value: '<2hrs', label: 'Response Time', icon: 'Clock' },
    { value: '15+', label: 'Technologies', icon: 'Code2' },
    { value: '100%', label: 'Quality Focus', icon: 'Target' },
    { value: '24/7', label: 'Support', icon: 'Shield' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact Byte Pairs | Free Software & Website Consultation</title>
        <meta name="description" content="Get in touch with Byte Pairs. Calculate project costs with our interactive estimator, schedule a call, or contact our software development team in Dharapuram." />
        <meta name="keywords" content="contact byte pairs, software consultation Dharapuram, web developers contact, project cost estimator India, business software quote, IT company Dharapuram" />
        <meta property="og:title" content="Contact Byte Pairs | Free Software & Website Consultation" />
        <meta property="og:description" content="Get in touch with Byte Pairs. Calculate project costs with our interactive estimator, schedule a call, or contact our software development team in Dharapuram." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/contact-engagement-hub" />
        <link rel="canonical" href="https://bytepairs.com/contact-engagement-hub" />

        {/* ContactPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Byte Pairs | Free Software & Website Consultation",
            "description": "Get in touch with Byte Pairs. Calculate project costs with our interactive estimator, schedule a call, or contact our software development team in Dharapuram.",
            "url": "https://bytepairs.com/contact-engagement-hub",
            "mainEntity": {
              "@type": "ProfessionalService",
              "name": "Byte Pairs",
              "telephone": "+916374241548",
              "email": "bytepairs2026@gmail.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Dharapuram",
                "addressLocality": "Dharapuram",
                "addressRegion": "Tamil Nadu",
                "postalCode": "638656",
                "addressCountry": "IN"
              }
            }
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://bytepairs.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Contact Us",
                "item": "https://bytepairs.com/contact-engagement-hub"
              }
            ]
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="MessageCircle" size={16} />
              <span>Let's Start a Conversation</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Ready to Transform Your
              <span className="text-gradient block">Digital Presence?</span>
            </h1>

            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
              Connect with our expert team through your preferred channel. From instant chat to detailed consultations,
              we're here to turn your vision into reality with transparent pricing and proven processes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
                onClick={() => handleTabSelect('contact')}
              >
                Start Free Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Calculator"
                iconPosition="left"
                className="border-primary/20 text-primary hover:bg-primary/5"
                onClick={() => handleTabSelect('estimator')}
              >
                Get Project Estimate
              </Button>
            </div>

            {/* Hero Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {heroStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Icon name={stat.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm text-text-secondary">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section ref={tabsRef} className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${activeTab === tab.id
                  ? 'bg-primary text-white shadow-brand'
                  : 'bg-muted text-text-secondary hover:bg-primary/10 hover:text-primary'
                  }`}
              >
                <Icon name={tab.icon} size={18} />
                <span>{tab.label}</span>
              </button>
            ))}
          </motion.div>

          {/* Tab Content */}
          <div className="space-y-12">
            {/* Main Content Area */}
            <div>
              {activeTab === 'contact' && (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  className="card p-8"
                >
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-foreground mb-2">Send Us a Message</h2>
                    <p className="text-text-secondary">
                      Fill out the form below and we'll get back to you within 2 hours during business hours.
                    </p>
                  </div>
                  <ContactForm />
                </motion.div>
              )}

              {activeTab === 'methods' && (
                <motion.div
                  key="methods"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <ContactMethods />
                </motion.div>
              )}

              {activeTab === 'estimator' && (
                <motion.div
                  key="estimator"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <ProjectEstimator />
                </motion.div>
              )}

              {activeTab === 'process' && (
                <motion.div
                  key="process"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <ConsultationProcess />
                </motion.div>
              )}
            </div>

            {/* Quick Contact & Locations - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Quick Contact Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="card p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20"
              >
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Zap" size={24} color="white" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Need Immediate Help?</h3>
                  <p className="text-sm text-text-secondary">Our team is online and ready to assist</p>
                </div>

                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Phone"
                    iconPosition="left"
                    className="border-primary/20 text-primary hover:bg-primary/5"
                    onClick={() => window.location.href = 'tel:+916374241548'}
                  >
                    Call Now: +91 6374241548
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="MessageCircle"
                    iconPosition="left"
                    className="border-success/20 text-success hover:bg-success/5"
                    onClick={() => window.open('https://wa.me/916374241548', '_blank')}
                  >
                    WhatsApp: +91 6374241548
                  </Button>
                </div>

                <div className="mt-4 pt-4 border-t border-primary/20 text-center">
                  <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    <span>Team online • Avg response: 1.2 hours</span>
                  </div>
                </div>
              </motion.div>

              {/* Our Locations - Just the location cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="card p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 text-center">Our Locations</h3>
                <div className="space-y-3 mb-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="MapPin" size={16} className="text-primary mt-1" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Headquarters</p>
                      <p className="text-xs text-text-secondary">Dharapuram, Tamil Nadu<br />India - 638656</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="Phone" size={16} className="text-primary mt-1" />
                    <a href="tel:+916374241548" className="text-sm text-primary hover:underline">
                      +91 6374241548
                    </a>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="Mail" size={16} className="text-primary mt-1" />
                    <a href="mailto:bytepairs2026@gmail.com" className="text-sm text-primary hover:underline">
                      bytepairs2026@gmail.com
                    </a>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="Clock" size={16} className="text-primary mt-1" />
                    <span className="text-sm text-text-secondary">IST (UTC+5:30)</span>
                  </div>
                </div>

                {/* Map */}
                <div className="h-48 rounded-lg overflow-hidden border border-border">
                  <iframe
                    width="100%"
                    height="100%"
                    loading="lazy"
                    title="Our Location"
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps?q=Dharapuram,Tamil+Nadu,India&z=14&output=embed"
                    className="border-0"
                  />
                </div>
              </motion.div>
            </div>

            {/* Working Hours - Centered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="max-w-2xl mx-auto"
            >
              <div className="card p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-foreground">Working Hours</h4>
                    <p className="text-sm text-text-secondary">When you can reach us</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-foreground">Monday - Friday</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-text-secondary">8:00 AM - 6:00 PM</span>
                      <div className="w-2 h-2 rounded-full bg-success" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-foreground">Saturday</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-text-secondary">10:00 AM - 2:00 PM</span>
                      <div className="w-2 h-2 rounded-full bg-success" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-foreground">Sunday</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-text-secondary">Emergency Only</span>
                      <div className="w-2 h-2 rounded-full bg-warning" />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Info" size={14} />
                    <span>All times shown in local timezone. Emergency support available 24/7 for critical issues.</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Support Channels - Centered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <h4 className="text-xl font-semibold text-foreground mb-6 text-center">Support Channels</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card p-6 bg-primary/10 border-2 border-primary/20 hover:border-primary/40 transition-all duration-300">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Icon name="MessageCircle" size={24} className="text-primary" />
                    </div>
                    <h5 className="font-semibold text-foreground mb-2">Live Chat</h5>
                    <p className="text-sm text-text-secondary mb-3">Instant responses during business hours</p>
                    <div className="space-y-1">
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Available:</span> Mon-Fri 9AM-6PM
                      </div>
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Response:</span> Immediate
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card p-6 bg-accent/10 border-2 border-accent/20 hover:border-accent/40 transition-all duration-300">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Icon name="Mail" size={24} className="text-accent" />
                    </div>
                    <h5 className="font-semibold text-foreground mb-2">Email Support</h5>
                    <p className="text-sm text-text-secondary mb-3">Detailed responses to complex queries</p>
                    <div className="space-y-1">
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Available:</span> 24/7
                      </div>
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Response:</span> &lt; 2 hours
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card p-6 bg-warning/10 border-2 border-warning/20 hover:border-warning/40 transition-all duration-300">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-warning/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Icon name="Phone" size={24} className="text-warning" />
                    </div>
                    <h5 className="font-semibold text-foreground mb-2">Phone Support</h5>
                    <p className="text-sm text-text-secondary mb-3">Direct line for urgent matters</p>
                    <div className="space-y-1">
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Available:</span> Mon-Fri 9AM-6PM
                      </div>
                      <div className="text-xs text-text-secondary">
                        <span className="font-medium">Response:</span> Immediate
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Team Status - Centered */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-gradient-to-r from-success/10 to-primary/10 rounded-xl p-6 border border-success/20">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                      <Icon name="Users" size={24} color="var(--color-success)" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full animate-pulse" />
                  </div>

                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-foreground mb-1">Team Status: Online</h4>
                    <p className="text-sm text-text-secondary mb-2">
                      Our team is currently online and ready to help with your project needs.
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-text-secondary">
                      <span>• Ready to serve</span>
                      <span>• Average response: 1.2 hours</span>
                      <span>• Available now</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SocialProof />
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ready to Start Your Digital Journey?
            </h2>
            <p className="text-xl text-text-secondary mb-8">
              Let's build something amazing together. Get in touch today and start your digital transformation journey.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Rocket"
                iconPosition="right"
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand"
                onClick={openConsultation}
              >
                Start Your Project Today
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="border-primary/20 text-primary hover:bg-primary/5"
              >
                Download Our Portfolio
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactEngagementHub;